/**
 * PdfCsvButtons (Plan 04-07 / RPT-01 / RPT-02).
 *
 * Per-candidate "Download PDF" (authed blob, BUG-006 filename from Content-Disposition) +
 * per-position "Export CSV" (authed blob). Both go through the api.ts authed-blob helper so
 * the admin JWT rides the request (a native <a download> can't send it).
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { mapErrorSlug } from "@/lib/error-slug-mapper";

import { downloadCandidatePdf, downloadPositionCsv } from "./api";

export interface PdfCsvButtonsProps {
  candidateId: number;
  positionId: number;
}

export function PdfCsvButtons(props: PdfCsvButtonsProps) {
  const { t } = useTranslation("rubric");
  const [busy, setBusy] = useState(false);

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      toast.error(mapErrorSlug(err) || t("reports.error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <span
      className="flex gap-2"
      data-testid={`pdf-csv-${props.candidateId}-${props.positionId}`}
    >
      <Button
        variant="outline"
        disabled={busy}
        onClick={() => run(() => downloadCandidatePdf(props.candidateId))}
        className="min-h-[44px]"
        data-testid="download-pdf"
      >
        {t("reports.pdf")}
      </Button>
      <Button
        variant="outline"
        disabled={busy}
        onClick={() => run(() => downloadPositionCsv(props.positionId))}
        className="min-h-[44px]"
        data-testid="export-csv"
      >
        {t("reports.csv")}
      </Button>
    </span>
  );
}
